/**
 * Attachment-drop transparency types — shared by the sent-mail detail
 * (persisted `sent_emails.attachmentDrops`) and the send-modal preflight
 * (`POST /api/v1/attachments/{eventId}/email-preflight`).
 *
 * ⚠ The INTENTIONAL-REASON SET mirrors main-consumer
 * `src/lib/attachment-drops.ts` — keep those two in sync; they decide whether a
 * send counts as degraded, so a divergence changes behaviour.
 *
 * The reason unions below are NOT a mirror of anything and deliberately so:
 * both layers tolerate unknown strings, which is what lets the backend add a
 * code without a coordinated release. They are a reading aid, and they will
 * lag.
 */

/** Known reason codes. Backend may grow new ones — always render via the
 *  `t.has(reason) ? t(reason) : t("unknown")` idiom, never exhaustively. */
export type AttachmentDropReason =
  | "walletIconMissing"
  | "walletIconNotPng"
  // Our own conversion queue shed the work, or storage did not answer. NOT a
  // statement about the operator's file — telling them their icon "is not a
  // PNG" when the real cause is load sends them to fix something that is fine.
  | "walletIconUnavailable"
  | "walletSigningUnavailable"
  | "walletCertExpired"
  | "walletCertMismatch"
  | "icsStartDateInvalid"
  | "guestUnavailable"
  | "renderFailed"
  | "zipBlocked"
  | "tooLarge"
  | "budgetExceeded"
  | "missingFile"
  | "conditionNotMatched"
  | "linkOnly"
  | "fetchFailed"
  | "resolveFailed";

/** Drops that reflect INTENDED configuration (per-guest targeting, portal-only
 *  access) — shown info-toned, never as a failure. Everything else (including
 *  unknown future codes) renders error-toned. */
export const INTENTIONAL_DROP_REASONS: ReadonlySet<string> = new Set([
  "conditionNotMatched",
  "linkOnly",
]);

export function isIntentionalDropReason(reason: string): boolean {
  return INTENTIONAL_DROP_REASONS.has(reason);
}

/** One persisted drop on a sent email (sent_emails.attachmentDrops element).
 *  `filename` can be empty for whole-resolve failures (reason `resolveFailed`). */
export interface AttachmentDrop {
  filename: string;
  reason: AttachmentDropReason | (string & {});
  attachmentName?: string;
}

/** Reason codes for a DELIVERED but incomplete attachment. Same open-string
 *  tolerance rule as AttachmentDropReason. */
export type AttachmentDegradedReason =
  // A container no decoder on the render path can read — in practice WebP.
  // It no longer means "not a PNG": JPEG is converted now.
  | "walletImageNotPng"
  | "walletImageUnreachable"
  // Refused before decoding, on the header's own pixel count.
  | "walletImageTooLarge"
  // The bytes claim a format we support and there is no image behind them.
  | "walletImageUndecodable"
  // Conversion was shed under load. The only one of these that a plain resend
  // is likely to fix, which is why it is not folded into its neighbours.
  | "walletImageBusy"
  // A field asked the device to format its date, but its value was prose
  // rather than one date variable, so the text shipped unchanged.
  | "walletDateStyleIgnored"
  // The operator's custom lock-screen window was unusable (a bound missing or
  // not an instant, or the end not after the start), so the pass surfaces
  // around the event's own times instead. Pass-level: `item` is empty.
  | "walletRelevanceWindowIgnored"
  // A row the operator configured resolved to nothing for THIS guest, so the
  // renderer left it out rather than draw a caption over a blank. Named by the
  // field's label, which is the only handle the operator has ever seen. The
  // one loss in the pass that used to happen in complete silence — a customer
  // compared their builder with their phone and found two rows missing.
  | "walletFieldDropped"
  // The event has no QR signing key, so the barcode carries the bare guest id.
  // The scanner rejects that — and every guard upstream waves it through,
  // because a bare id is neither empty nor an unresolved token.
  | "walletBarcodeUnsigned"
  // The pass ships, just without a code — it moved here from the drop union
  // when an empty barcode stopped failing the whole pass. Leaving it there
  // would have rendered "delivered without its barcode" underneath the
  // heading "was not delivered with this email".
  | "walletBarcodeEmpty";

/** One delivered-but-incomplete attachment (sent_emails.attachmentDegraded).
 *  Deliberately NOT an AttachmentDrop: the drop list is rendered as "was not
 *  delivered", and this attachment WAS. */
export interface AttachmentDegraded {
  filename: string;
  /** The part that is missing, e.g. `logo.png`. */
  item: string;
  reason: AttachmentDegradedReason | (string & {});
  attachmentName?: string;
}

/** One predicted non-delivery from the send-modal preflight. */
export interface EmailPreflightWarning {
  code: AttachmentDropReason | (string & {});
  attachmentId?: string;
  attachmentName?: string;
  docTemplateId?: string;
  filename?: string;
}

export interface EmailPreflightResult {
  warnings: EmailPreflightWarning[];
}
